import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { compareDateTimes, formatInputDate } from "../utils/Time";
import { formatSplitKey } from "../commons/FormatValue";
import { filteredItemInRange, findItemInArray } from "../utils/ManageArray";
import { initializeSetOption } from "../utils/InitializeSetDefault";
import {
  FloatingControlComponent,
  FormCheckComponent,
  SearchSelectComponent
} from "./FormComponent";
import { useNavigate } from "react-router-dom";

const SearchFilter = ({
  data,
  inputField,
  moreButton,
  searchValue,
  fieldClassName,
  searchBtnOnClick,
  clearBtnOnClick,
  searchCallback,
  backButton
}) => {
  const navigate = useNavigate();
  const searchBtnRef = useRef(null);
  const clearBtnRef = useRef(null);
  const [optionData, setOptionData] = useState({});
  const [searchData, setSearchData] = useState(inputField);

  const setDefaultOption = () => {
    inputField?.forEach(({ name, labelName }) =>
      setOptionData((prev) => ({
        ...prev,
        [name]: initializeSetOption({
          key: name,
          label: labelName || name,
          dataset: data
        })
      }))
    );
  };

  const setDateBetween = (search) =>
    search?.filter(
      ({ name, type }) =>
        type === "date" &&
        ["START", "END", "createdAt", "updatedAt"].some((suffix) =>
          name.toUpperCase().includes(suffix.toUpperCase())
        )
    );

  const handleChange = (name, value, queryService) => {
    const updatedData = searchData?.map((item) =>
      item.name === name ? { ...item, value } : { ...item, noneAsync: true }
    );

    if (queryService) {
      const [start, end] = setDateBetween(updatedData);
      const searchStart = start?.value ? `&start=${start?.value}` : "";
      const searchEnd = end?.value ? `&end=${end?.value}` : "";
      queryService(`?limit=1000${searchStart}${searchEnd}`);
    }
    setSearchData(updatedData);

    if (!fieldClassName?.actionButton) {
      return searchCallback(updatedData);
    }
  };

  const autoDisplayValue = ({ name, type, findValue, value }) => {
    if (!findValue) return value;

    const matchingItem = data?.find((item) =>
      searchData?.some(({ name, type, value }) => {
        if (name === findValue?.value) {
          const dateValue = new Date(value);
          const splitKey = formatSplitKey(findValue?.value, ".", item);
          return type === "date" ? splitKey === dateValue : splitKey === value;
        }
      })
    );

    const matchingValue = formatSplitKey(name, ".", matchingItem);
    return matchingItem
      ? type === "date"
        ? formatInputDate(matchingValue, "Local").date
        : matchingValue
      : null;
  };

  const checkTypeFilteredData = (search) => {
    const [start, end] = setDateBetween(search);

    return filteredItemInRange(data, search, start?.name, end?.name).filter(
      (item) =>
        search
          ?.filter(({ value }) => value)
          .every((search) => compareDateTimes(item, search, start, end))
    );
  };

  const updateOptionData = (search, input, prevData) => {
    return search
      ?.filter(({ value }) => value)
      .reduce(
        (updatedData, item) => {
          return input
            ?.filter(({ name }) => name !== item.name)
            ?.reduce(
              (acc, input) => ({
                ...acc,
                [input.name]: initializeSetOption({
                  key: input.name,
                  label: input.labelName || input.name,
                  dataset: checkTypeFilteredData(search)
                })
              }),
              updatedData
            );
        },
        { ...prevData }
      );
  };

  const onSearchCallback = (event, name) => {
    event.preventDefault();

    if (name === "clear") {
      setSearchData(inputField);
      setDefaultOption();
      return searchCallback([]);
    }

    searchCallback(searchData);
  };

  useEffect(() => {
    setDefaultOption();
    if (searchValue?.length && !searchData.some(({ value }) => value)) {
      setSearchData(
        searchData?.map((item) =>
          ((matchingSearchItem) =>
            matchingSearchItem
              ? { ...item, value: matchingSearchItem.value }
              : item)(searchValue.find(({ name }) => item.name === name))
        )
      );
    }
  }, [data]);

  useEffect(() => {
    if (inputField?.filter(({ value }) => value)?.length !== 0) {
      setSearchData(inputField);
    }
  }, [inputField]);

  useEffect(() => {
    setOptionData((prev) => updateOptionData(searchData, inputField, prev));
  }, [searchData, data]);

  useEffect(() => {
    setTimeout(() => {
      if (searchBtnOnClick) searchBtnRef.current.click();
      if (clearBtnOnClick) clearBtnRef.current.click();
    }, 100);
  }, [searchBtnOnClick, clearBtnOnClick]);

  return (
    <Form onSubmit={onSearchCallback}>
      <Row
        className={
          fieldClassName?.inputRow ||
          "justify-content-center align-items-center"
        }
      >
        {searchData.map(
          (
            {
              name,
              label,
              classNames,
              type,
              size,
              min,
              max,
              button,
              readOnly,
              disabled,
              filterBy,
              findValue,
              noneAsync,
              queryService,
              minKeywordLength,
              value
            },
            idx
          ) => {
            const keyMaxMin = min || max;
            const valueMaxMin = findItemInArray(
              searchData,
              "name",
              keyMaxMin
            )?.value;
            const displayValue = autoDisplayValue({
              name,
              type,
              findValue,
              value
            });

            return (
              <Col
                key={`${name}-${idx}`}
                sm={size ? size : size === null ? null : 3}
                className={classNames?.col}
              >
                {button ? (
                  button
                ) : !type || type === "select" ? (
                  <SearchSelectComponent
                    placeholder="ทั้งหมด"
                    labelField={label}
                    disabled={disabled}
                    noneAsync={noneAsync}
                    filterBy={filterBy}
                    minKeywordLength={minKeywordLength}
                    floatingClass={classNames?.label}
                    queryService={queryService}
                    options={optionData?.[name]}
                    onChange={(change) =>
                      handleChange(name, change?.value || null)
                    }
                    value={(findValue && displayValue) || value}
                  />
                ) : ["checkbox", "radio"].includes(type) ? (
                  <FormCheckComponent
                    type={type}
                    name={name}
                    label={label}
                    readOnly={readOnly}
                    disabled={disabled}
                    className={{ ...classNames, check: "py-3" }}
                    onChange={(e) => handleChange(name, e.target.checked)}
                    checked={!!value}
                  />
                ) : (
                  <FloatingControlComponent
                    type={type}
                    name={name}
                    label={label}
                    readOnly={readOnly}
                    disabled={disabled}
                    className={classNames}
                    placeholder="โปรดระบุ"
                    min={min ? valueMaxMin : null}
                    max={max ? valueMaxMin : null}
                    onChange={(e) =>
                      handleChange(name, e.target.value, queryService)
                    }
                    value={displayValue || ""}
                  />
                )}
              </Col>
            );
          }
        )}

        {fieldClassName?.actionButton && (
          <Col
            sm={fieldClassName.actionRowSize ?? 3}
            className={fieldClassName.actionButton}
          >
            {moreButton && moreButton}

            <Button
              ref={searchBtnRef}
              type="submit"
              variant="success"
              disabled={fieldClassName.actionDisabled?.start}
              className="me-2 py-2 px-4 text-dark rounded-pill"
            >
              {fieldClassName?.actionName?.start || "ค้นหา"}
            </Button>
            <Button
              ref={clearBtnRef}
              variant="outline-secondary"
              disabled={fieldClassName.actionDisabled?.end}
              className="py-2 px-4 text-dark rounded-pill"
              onClick={(event) => onSearchCallback(event, "clear")}
            >
              {fieldClassName?.actionName?.end || "ล้างการค้นหา"}
            </Button>

            {backButton && (
              <Button
                variant="outline-secondary"
                className="ms-2 px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                onClick={() =>
                  navigate(backButton.path, {
                    state: { name: backButton.state }
                  })
                }
              >
                ย้อนกลับ
              </Button>
            )}
          </Col>
        )}
      </Row>
    </Form>
  );
};

SearchFilter.propTypes = {
  data: PropTypes.array.isRequired,
  inputField: PropTypes.array.isRequired,
  moreButton: PropTypes.element,
  searchValue: PropTypes.array,
  fieldClassName: PropTypes.object,
  searchBtnOnClick: PropTypes.bool,
  clearBtnOnClick: PropTypes.bool,
  searchCallback: PropTypes.func,
  backButton: PropTypes.string
};

export default SearchFilter;
